import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { SettingsSaveRow } from "@/pages/stammdaten/settings/SettingsSaveRow";
import {
  DEFAULT_PRICING_POLICY,
  errorMessage,
  type EarlyBookingBand,
  type OccupancyPriceBand,
  type PricingPolicySettings,
  type SettingsSectionProps,
  type TenantSettings,
} from "@/pages/stammdaten/settings/tenantSettingsTypes";

function asText(value: string | number): string {
  return String(value);
}

function policyFrom(settings: TenantSettings): PricingPolicySettings {
  const raw = settings.pricing_policy;
  if (!raw) return DEFAULT_PRICING_POLICY;
  return {
    dynamic_enabled: raw.dynamic_enabled,
    dynamic_use_previous_year: raw.dynamic_use_previous_year,
    dynamic_use_current_bookings: raw.dynamic_use_current_bookings,
    dynamic_bands: raw.dynamic_bands.map((band) => ({
      min_occupancy_percent: band.min_occupancy_percent,
      surcharge_percent: asText(band.surcharge_percent),
    })),
    early_booking_enabled: raw.early_booking_enabled,
    early_booking_bands: raw.early_booking_bands.map((band) => ({
      min_days_before_arrival: band.min_days_before_arrival,
      adjustment_percent: asText(band.adjustment_percent),
    })),
  };
}

export function PricingPolicySection({ settings, canWrite, onSaved }: SettingsSectionProps) {
  const [policy, setPolicy] = useState<PricingPolicySettings>(policyFrom(settings));
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPolicy(policyFrom(settings));
  }, [settings]);

  function updateDynamicBand(index: number, patch: Partial<OccupancyPriceBand>) {
    setPolicy((current) => ({
      ...current,
      dynamic_bands: current.dynamic_bands.map((band, bandIndex) =>
        bandIndex === index ? { ...band, ...patch } : band,
      ),
    }));
  }

  function updateEarlyBand(index: number, patch: Partial<EarlyBookingBand>) {
    setPolicy((current) => ({
      ...current,
      early_booking_bands: current.early_booking_bands.map((band, bandIndex) =>
        bandIndex === index ? { ...band, ...patch } : band,
      ),
    }));
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await api.patch<TenantSettings>("/api/v1/settings/pricing-policy", {
        dynamic_enabled: policy.dynamic_enabled,
        dynamic_use_previous_year: policy.dynamic_use_previous_year,
        dynamic_use_current_bookings: policy.dynamic_use_current_bookings,
        dynamic_bands: policy.dynamic_bands.map((band) => ({
          min_occupancy_percent: Number(band.min_occupancy_percent),
          surcharge_percent: band.surcharge_percent.trim() || "0",
        })),
        early_booking_enabled: policy.early_booking_enabled,
        early_booking_bands: policy.early_booking_bands.map((band) => ({
          min_days_before_arrival: Number(band.min_days_before_arrival),
          adjustment_percent: band.adjustment_percent.trim() || "0",
        })),
      });
      setSuccess(true);
      onSaved();
    } catch (err) {
      setError(errorMessage(err, "Preisregeln konnten nicht gespeichert werden"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Preisgestaltung"
      description="Zusätzlich zum hinterlegten Artikelpreis. Ohne aktivierte Regel bleibt der Artikelpreis unverändert. Sind beide Regeln aktiv, werden sie multipliziert."
      bodyClassName="space-y-4"
    >
      <form onSubmit={(e) => void onSave(e)} className="space-y-6">
        <section className="space-y-3">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={policy.dynamic_enabled}
              onChange={(e) =>
                setPolicy((current) => ({ ...current, dynamic_enabled: e.target.checked }))
              }
              disabled={!canWrite}
            />
            <span>
              <span className="block font-medium text-label">Dynamic Pricing</span>
              <span className="block text-label-secondary">
                Tage, die im Vorjahr stark belegt waren oder für die schon viele Reservierungen vorliegen, werden teurer.
                Es gilt der höhere der beiden Werte. Gezählt werden dieselben Platztypen wie in der Dashboard-Belegung.
              </span>
            </span>
          </label>
          {policy.dynamic_enabled ? (
            <div className="space-y-3 pl-6">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={policy.dynamic_use_previous_year}
                  onChange={(e) =>
                    setPolicy((current) => ({
                      ...current,
                      dynamic_use_previous_year: e.target.checked,
                    }))
                  }
                  disabled={!canWrite}
                />
                Auslastung derselben Kalendertage im Vorjahr
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={policy.dynamic_use_current_bookings}
                  onChange={(e) =>
                    setPolicy((current) => ({
                      ...current,
                      dynamic_use_current_bookings: e.target.checked,
                    }))
                  }
                  disabled={!canWrite}
                />
                Schon vorliegende Reservierungen für diese Tage
              </label>
              <div className="space-y-2">
                <p className="text-xs font-medium text-label-tertiary">Aufschlag ab dieser Auslastung</p>
                {policy.dynamic_bands.map((band, index) => (
                  <div key={index} className="flex flex-wrap items-end gap-2">
                    <label className="flex w-36 flex-col gap-1 text-sm">
                      Auslastung ab %
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        required
                        className="eh-input"
                        value={band.min_occupancy_percent}
                        onChange={(e) =>
                          updateDynamicBand(index, {
                            min_occupancy_percent: Number(e.target.value),
                          })
                        }
                        disabled={!canWrite}
                      />
                    </label>
                    <label className="flex w-36 flex-col gap-1 text-sm">
                      Aufschlag %
                      <input
                        type="number"
                        min="0"
                        max="200"
                        step="0.01"
                        required
                        className="eh-input"
                        value={band.surcharge_percent}
                        onChange={(e) =>
                          updateDynamicBand(index, { surcharge_percent: e.target.value })
                        }
                        disabled={!canWrite}
                      />
                    </label>
                    {canWrite && policy.dynamic_bands.length > 1 ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setPolicy((current) => ({
                            ...current,
                            dynamic_bands: current.dynamic_bands.filter((_, bandIndex) => bandIndex !== index),
                          }))
                        }
                      >
                        Entfernen
                      </Button>
                    ) : null}
                  </div>
                ))}
                {canWrite ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      setPolicy((current) => ({
                        ...current,
                        dynamic_bands: [
                          ...current.dynamic_bands,
                          { min_occupancy_percent: 0, surcharge_percent: "0" },
                        ],
                      }))
                    }
                  >
                    Stufe hinzufügen
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
        </section>

        <section className="space-y-3">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={policy.early_booking_enabled}
              onChange={(e) =>
                setPolicy((current) => ({ ...current, early_booking_enabled: e.target.checked }))
              }
              disabled={!canWrite}
            />
            <span>
              <span className="block font-medium text-label">Vorteil für Frühbucher</span>
              <span className="block text-label-secondary">
                Je später gebucht wird, desto teurer wird der Aufenthalt. Negative Prozent sind ein Nachlass,
                positive ein Aufschlag. Maßgeblich ist der Tag der Buchung, nicht ein späteres Ändern der Buchung.
              </span>
            </span>
          </label>
          {policy.early_booking_enabled ? (
            <div className="space-y-2 pl-6">
              <p className="text-xs font-medium text-label-tertiary">
                Es gilt die Stufe mit den meisten Tagen, die der Vorlauf noch erreicht.
              </p>
              {policy.early_booking_bands.map((band, index) => (
                <div key={index} className="flex flex-wrap items-end gap-2">
                  <label className="flex w-44 flex-col gap-1 text-sm">
                    Mindestens Tage vorher
                    <input
                      type="number"
                      min="0"
                      max="730"
                      step="1"
                      required
                      className="eh-input"
                      value={band.min_days_before_arrival}
                      onChange={(e) =>
                        updateEarlyBand(index, {
                          min_days_before_arrival: Number(e.target.value),
                        })
                      }
                      disabled={!canWrite}
                    />
                  </label>
                  <label className="flex w-40 flex-col gap-1 text-sm">
                    Preisänderung %
                    <input
                      type="number"
                      min="-90"
                      max="200"
                      step="0.01"
                      required
                      className="eh-input"
                      value={band.adjustment_percent}
                      onChange={(e) =>
                        updateEarlyBand(index, { adjustment_percent: e.target.value })
                      }
                      disabled={!canWrite}
                    />
                  </label>
                  {canWrite && policy.early_booking_bands.length > 1 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setPolicy((current) => ({
                          ...current,
                          early_booking_bands: current.early_booking_bands.filter(
                            (_, bandIndex) => bandIndex !== index,
                          ),
                        }))
                      }
                    >
                      Entfernen
                    </Button>
                  ) : null}
                </div>
              ))}
              {canWrite ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setPolicy((current) => ({
                      ...current,
                      early_booking_bands: [
                        ...current.early_booking_bands,
                        { min_days_before_arrival: 0, adjustment_percent: "0" },
                      ],
                    }))
                  }
                >
                  Stufe hinzufügen
                </Button>
              ) : null}
            </div>
          ) : null}
        </section>

        <SettingsSaveRow
          canWrite={canWrite}
          saving={saving}
          success={success}
          successText="Preisregeln gespeichert."
          error={error}
          label="Preisregeln speichern"
        />
      </form>
    </Card>
  );
}
